import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Paper,
  Typography,
  Stack,
  Skeleton,
  alpha,
  useTheme,
  Card,
  CardContent,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Collapse,
  Badge,
  Chip
} from '@mui/material';
import {
  History as HistoryIcon,
  Assignment as AssignmentIcon,
  Cancel as CancelIcon,
  Done as DoneIcon,
  ForwardToInbox as ForwardToInboxIcon,
  CheckCircle as CheckCircleIcon,
  ThumbUp as ThumbUpIcon,
  Circle as CircleIcon,
  FiberManualRecord as DotIcon,
  Comment as CommentIcon,
  Add as AddIcon,
  Send as SendIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import LocalizedDate from '../common/LocalizedDate';
import { getActivityIcon, getActivityTypeText, getActivityColors } from '../../utils/taskUtils';
import { API_BASE_URL } from '../../config';

// Icon mapping
const iconComponents = {
  History: HistoryIcon,
  Assignment: AssignmentIcon,
  Cancel: CancelIcon,
  Done: DoneIcon,
  ForwardToInbox: ForwardToInboxIcon,
  CheckCircle: CheckCircleIcon,
  ThumbUp: ThumbUpIcon,
  Circle: CircleIcon,
  FiberManualRecord: DotIcon
};

export const ActivityTimeline = ({ activities, loading, error, task }) => {
  const theme = useTheme();
  const { t, i18n } = useTranslation(['tasks', 'documents']);
  const { user } = useAuth();
  const isRtl = i18n.language === 'dari' || i18n.language === 'ps';
  
  // Comment management state
  const [comments, setComments] = useState({});
  const [loadingComments, setLoadingComments] = useState({});
  const [showComments, setShowComments] = useState({});
  const [newComment, setNewComment] = useState({});
  const [addingComment, setAddingComment] = useState(null);
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);

  // Fetch comments for a document
  const fetchComments = async (documentId) => {
    if (!documentId || !user?.token || loadingComments[documentId]) return;
    
    setLoadingComments(prev => ({ ...prev, [documentId]: true }));
    
    try {
      const response = await axios.get(
        `${API_BASE_URL}/documents/${documentId}/comments`,
        { headers: { Authorization: `Bearer ${user.token}` }}
      );
      
      const commentsData = response.data || [];
      setComments(prev => ({ ...prev, [documentId]: commentsData }));
    } catch (error) {
      console.error('Error fetching comments:', error);
    } finally {
      setLoadingComments(prev => ({ ...prev, [documentId]: false }));
    }
  };

  // Add a new comment with step association
  const addComment = async (documentId, content, stepId) => {
    if (!content.trim() || !user?.token) return;
    
    try {
      const response = await axios.post(
        `${API_BASE_URL}/documents/${documentId}/comments`,
        { 
          content: content.trim(),
          metadata: { 
            step_id: stepId,
            step_type: 'timeline_step',
            added_from: 'activity_timeline'
          }
        },
        { headers: { Authorization: `Bearer ${user.token}` }}
      );
      
      // Refresh comments after adding
      await fetchComments(documentId);
      setNewComment(prev => ({ ...prev, [stepId]: '' }));
      setAddingComment(null);
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };

  // Toggle comments visibility for specific step
  const toggleComments = (documentId, stepId) => {
    const isCurrentlyShown = showComments[stepId];
    setShowComments(prev => ({ ...prev, [stepId]: !isCurrentlyShown }));
  };

  // Auto-fetch comments when component mounts or task changes
  useEffect(() => {
    if (task?.document_id && user?.token) {
      fetchComments(task.document_id);
    }
  }, [task?.document_id, user?.token]);

  if (loading) {
    return (
      <Paper elevation={2} sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ 
          height: '4px',
          background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`
        }} />
        <Box sx={{ p: 2.5 }}>
          <Stack spacing={2}>
            <Skeleton variant="text" width="40%" height={24} />
            {[1, 2, 3].map((i) => (
              <Box key={i} sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Skeleton variant="circular" width={32} height={32} />
                <Box flex={1}>
                  <Skeleton variant="text" width="60%" height={20} />
                  <Skeleton variant="text" width="40%" height={16} />
                </Box>
              </Box>
            ))}
          </Stack>
        </Box>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper elevation={1} sx={{ mb: 3, p: 2.5, borderRadius: 2, borderLeft: 3, borderColor: 'error.main' }}>
        <Typography variant="body2" color="error">
          {t('documents:error_loading_activities')}
        </Typography>
      </Paper>
    );
  }

  // Process activities for timeline display
  const processedActivities = activities.filter(activity => 
    ['ASSIGNED', 'RECEIVED', 'FORWARDED', 'COMPLETED', 'REJECTED', 'RECEIPT_REJECTED', 'STATUS_CHANGED'].includes(activity.type)
  );

  // Group related events and assign step numbers
  const groupedEvents = [];
  const processedEventKeys = new Set();
  
  processedActivities.forEach((event, index) => {
    // Handle rejection events to extract reason from metadata FIRST
    if ((event.type === 'REJECTED' || event.type === 'RECEIPT_REJECTED' ||
        (event.type === 'STATUS_CHANGED' && (event.metadata?.status === 'REJECTED' || event.action === 'TASK_REJECTED'))) && event.metadata) {
      try {
        const metadataObj = typeof event.metadata === 'string' ? JSON.parse(event.metadata) : event.metadata;
        console.log('Processing rejection event metadata:', metadataObj);

        // Try multiple possible field names for rejection reason
        const rejectionReason = metadataObj.reason || metadataObj.notes || metadataObj.remarks || 
                               metadataObj.comment || metadataObj.description || metadataObj.message;

        if (rejectionReason) {
          console.log('Found rejection reason:', rejectionReason);
          event.reason = rejectionReason;
          event.note = rejectionReason;
        } else {
          console.log('No rejection reason found in metadata');
          // Try to extract from description if it contains rejection info
          if (event.description && event.description.includes('rejected')) {
            const reasonMatch = event.description.match(/rejected.*?:\s*(.+)/i);
            if (reasonMatch && reasonMatch[1]) {
              console.log('Extracted rejection reason from description:', reasonMatch[1]);
              event.reason = reasonMatch[1].trim();
              event.note = reasonMatch[1].trim();
            }
          }
        }
      } catch (err) {
        console.error('Error parsing rejection event metadata:', err);
        // Fallback: try to extract reason via regex if metadata is a string
        if (typeof event.metadata === 'string') {
          // Try multiple regex patterns to extract reason
          const patterns = [
            /"reason"\s*:\s*"([^"]*)"/,
            /"notes"\s*:\s*"([^"]*)"/,
            /"remarks"\s*:\s*"([^"]*)"/,
            /"comment"\s*:\s*"([^"]*)"/,
            /"description"\s*:\s*"([^"]*)"/
          ];
          
          for (const pattern of patterns) {
            const match = event.metadata.match(pattern);
            if (match && match[1]) {
              console.log('Found rejection reason via regex:', match[1]);
              event.reason = match[1];
              event.note = match[1];
              break;
            }
          }
        }
      }
    }
    
    // FINAL fallback: if still no reason for REJECTED events, use task.notes
    if ((event.type === 'REJECTED' || event.type === 'RECEIPT_REJECTED' || 
         (event.type === 'STATUS_CHANGED' && event.metadata?.status === 'REJECTED')) && !event.reason && task && task.notes) {
      event.reason = task.notes;
      event.note = task.notes;
    }
    
    // Create a more specific key to avoid duplicates
    // For rejection events, include the reason in the key to distinguish between different rejections
    let eventKey = `${event.type}-${event.action}-${event.timestamp}-${event.userName}`;
    
    // For rejection events, include the reason to distinguish between different rejections
    if (event.type === 'REJECTED' || event.type === 'RECEIPT_REJECTED' || 
        (event.type === 'STATUS_CHANGED' && event.metadata?.status === 'REJECTED')) {
      const rejectionReason = event.metadata?.reason || event.reason || event.note || '';
      eventKey = `${event.type}-${event.action}-${event.timestamp}-${event.userName}-${rejectionReason}`;
    }
    
    if (processedEventKeys.has(eventKey)) return;
    
    // Skip rejection events that don't have a proper rejection reason
    if ((event.type === 'REJECTED' || event.type === 'RECEIPT_REJECTED' || 
         (event.type === 'STATUS_CHANGED' && event.metadata?.status === 'REJECTED')) && 
        (!event.reason || event.reason === 'rejected the task: unnamed task' || event.reason.includes('unnamed task'))) {
      console.log('Skipping rejection event without proper reason:', event);
      return;
    }
    
    processedEventKeys.add(eventKey);
    event.stepNumber = processedActivities.length - index;
    
    // Enhance the event with better display information
    if (event.type === 'STATUS_CHANGED' && event.status === 'COMPLETED') {
      event.displayType = 'COMPLETED';
      event.reason = event.completionInfo?.completion_notes || event.reason || event.note;
    }
    
    if (event.type === 'RECEIVED' || event.action === 'DOCUMENT_RECEIVED') {
      event.reason = t('tasks:document_physically_received', 'Document physically received');
    }
    
    groupedEvents.push(event);
  });

  // Sort by timestamp, newest first
  groupedEvents.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

  return (
    <Paper 
      elevation={0} 
      sx={{ 
      mb: 3, 
        border: `2px solid ${theme.palette.grey[200]}`,
        borderRadius: 1,
      overflow: 'hidden',
        backgroundColor: '#fafafa',
        direction: isRtl ? 'rtl' : 'ltr'
      }}
    >
      {/* Government-style Header */}
      <Box sx={{
        backgroundColor: theme.palette.grey[100],
        borderBottom: `1px solid ${theme.palette.grey[300]}`,
        px: 3,
        py: 2
      }}>
        <Typography 
          variant="h6" 
          sx={{ 
            fontWeight: 700,
            color: theme.palette.grey[800],
            fontSize: '1.1rem',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            direction: isRtl ? 'rtl' : 'ltr'
          }}
        >
          <HistoryIcon sx={{ fontSize: 20, color: theme.palette.grey[600] }} />
          {t('documents:documentHistory', 'Document History')}
          <Box 
            component="span" 
            sx={{ 
              backgroundColor: theme.palette.primary.main,
              color: 'white',
              px: 1,
              py: 0.25,
              borderRadius: 0.5,
              fontSize: '0.7rem',
              fontWeight: 600,
              ml: isRtl ? 0 : 'auto',
              mr: isRtl ? 'auto' : 0
            }}
          >
            {groupedEvents.length}
          </Box>
            </Typography>
      </Box>

      {/* Government-style Table Layout */}
      <TableContainer>
        <Table size="small" sx={{ 
          '& .MuiTableCell-root': { 
            borderBottom: `1px solid ${theme.palette.grey[200]}`,
            textAlign: isRtl ? 'right' : 'left'
          } 
        }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: theme.palette.grey[50] }}>
              <TableCell sx={{ 
                fontWeight: 700, 
                fontSize: '0.8rem', 
                textTransform: 'uppercase', 
                color: theme.palette.grey[700], 
                width: '50px', 
                textAlign: 'center'
              }}>
                #
              </TableCell>
              <TableCell sx={{ 
                fontWeight: 700, 
                fontSize: '0.8rem', 
                textTransform: 'uppercase', 
                color: theme.palette.grey[700], 
                width: '120px',
                textAlign: isRtl ? 'right' : 'left'
              }}>
                {t('tasks:action', 'Action')}
              </TableCell>
              <TableCell sx={{ 
                fontWeight: 700, 
                fontSize: '0.8rem', 
                textTransform: 'uppercase', 
                color: theme.palette.grey[700], 
                width: '120px',
                textAlign: isRtl ? 'right' : 'left'
              }}>
                {t('tasks:assigned_byy', 'Assigned By')}
              </TableCell>
              <TableCell sx={{ 
                fontWeight: 700, 
                fontSize: '0.8rem', 
                textTransform: 'uppercase', 
                color: theme.palette.grey[700],
                textAlign: isRtl ? 'right' : 'left'
              }}>
                {t('tasks:details', 'Details')}
              </TableCell>
              <TableCell sx={{ 
                fontWeight: 700, 
                fontSize: '0.8rem', 
                textTransform: 'uppercase', 
                color: theme.palette.grey[700], 
                width: '140px',
                textAlign: isRtl ? 'right' : 'left'
              }}>
                {t('tasks:date_time', 'Date & Time')}
              </TableCell>
              <TableCell sx={{ 
                fontWeight: 700, 
                fontSize: '0.8rem', 
                textTransform: 'uppercase', 
                color: theme.palette.grey[700], 
                width: '100px',
                textAlign: 'center'
              }}>
                {t('tasks:comments', 'Comments')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
        {groupedEvents.length > 0 ? (
              groupedEvents.map((event, index) => (
                <GovernmentActivityRow 
                  key={event.id || index}
                  event={event} 
                  index={groupedEvents.length - index} // Reverse numbering: start from total count, go down
                  theme={theme} 
                  t={t} 
                  isRtl={isRtl} 
                  task={task}
                  documentId={task?.document?.id || task?.document_id}
                  comments={comments}
                  loadingComments={loadingComments}
                  showComments={showComments}
                  toggleComments={toggleComments}
                  addComment={addComment}
                  newComment={newComment}
                  setNewComment={setNewComment}
                  addingComment={addingComment}
                  setAddingComment={setAddingComment}
                />
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                    <HistoryIcon sx={{ fontSize: 32, color: theme.palette.grey[400] }} />
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      {t('documents:no_activities', 'No activities recorded')}
            </Typography>
          </Box>
                </TableCell>
              </TableRow>
        )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

const GovernmentActivityRow = ({ 
  event, 
  index, 
  theme, 
  t, 
  isRtl, 
  task, 
  documentId,
  comments,
  loadingComments,
  showComments,
  toggleComments,
  addComment,
  newComment,
  setNewComment,
  addingComment,
  setAddingComment
}) => {
  const IconComponent = iconComponents[getActivityIcon(event)] || CircleIcon;
  const colors = getActivityColors(event, theme);
  const typeText = getActivityTypeText(event, t);
  
  // Generate stepId and log it
  const stepId = event.id || `${event.type}-${event.timestamp}`;
  console.log('Row stepId:', stepId, 'Event:', event.type, 'Event ID:', event.id);

  // Get status color for government style
  const getStatusColor = (eventType, displayType) => {
    // Handle display type for completed events
    if (displayType === 'COMPLETED' || eventType === 'COMPLETED') {
      return '#2e7d32'; // Dark green for completed
    }
    
    switch (eventType) {
      case 'COMPLETED':
        return '#2e7d32'; // Dark green for completed
      case 'RECEIVED':
        return '#81c784'; // Light green for physically received
      case 'REJECTED':
      case 'RECEIPT_REJECTED':
        return theme.palette.error.main;
      case 'FORWARDED':
        return theme.palette.warning.main;
      case 'ASSIGNED':
        return theme.palette.info.main;
      case 'STATUS_CHANGED':
        // Check if it's a completion status change
        if (event.metadata?.status === 'COMPLETED' || event.action === 'TASK_COMPLETED') {
          return '#2e7d32'; // Dark green for completed
        }
        return theme.palette.info.main;
      default:
        return theme.palette.grey[600];
    }
  };

  const statusColor = getStatusColor(event.type, event.displayType);

  // Format details with colored elements
  const getDetailsElements = () => {
    const elements = [];
    
    // Add forward/assignment info with colored users
    if (event.type === 'FORWARDED' && (event.forwardInfo?.to || event.metadata?.to)) {
      const targetUser = event.forwardInfo?.to || event.metadata?.to;
      elements.push(
        <Box component="span" key="forward" sx={{ display: 'inline' }}>
          {t('tasks:forwarded_to', 'Forwarded to')}: 
          <Box component="span" sx={{ 
            color: theme.palette.info.main,
            fontWeight: 600,
            backgroundColor: alpha(theme.palette.info.main, 0.1),
            px: 0.5,
            py: 0.2,
            borderRadius: 0.5,
            ml: 0.5,
            fontSize: '0.75rem'
          }}>
            {targetUser}
          </Box>
        </Box>
      );
    }
    
    if (event.type === 'ASSIGNED' && (event.assignmentInfo?.assignee_name || event.metadata?.assignee_name)) {
      const assigneeName = event.assignmentInfo?.assignee_name || 
                          event.metadata?.assignee_name ||
                          (event.metadata?.assignee && (event.metadata.assignee.full_name || event.metadata.assignee.username)) || 
                          task?.assignee?.full_name || 
                          task?.assignee?.username || 
                          t('documents:unknown_user');
      elements.push(
        <Box component="span" key="assign" sx={{ display: 'inline' }}>
          {t('tasks:assigned_to', 'Assigned to')}: 
          <Box component="span" sx={{ 
            color: theme.palette.success.main,
            fontWeight: 600,
            backgroundColor: alpha(theme.palette.success.main, 0.1),
            px: 0.5,
            py: 0.2,
            borderRadius: 0.5,
            ml: 0.5,
            fontSize: '0.75rem'
          }}>
            {assigneeName}
          </Box>
        </Box>
      );
    }

    // Add reason/notes with different coloring
    if (event.reason || event.note) {
      elements.push(
        <Box component="span" key="reason" sx={{ display: 'inline' }}>
          {t('tasks:reason', 'Reason')}: 
          <Box component="span" sx={{ 
            color: theme.palette.warning.main,
            fontWeight: 500,
            backgroundColor: alpha(theme.palette.warning.main, 0.08),
            px: 0.5,
            py: 0.2,
            borderRadius: 0.5,
            ml: 0.5,
            fontSize: '0.75rem',
            fontStyle: 'italic'
          }}>
            {event.reason || event.note}
          </Box>
        </Box>
      );
    }

    return elements;
  };

  return (
    <TableRow 
      sx={{
        '&:hover': {
          backgroundColor: alpha(statusColor, 0.02) 
        },
        '&:nth-of-type(even)': {
          backgroundColor: theme.palette.grey[25] || 'rgba(0, 0, 0, 0.01)'
        }
      }}
    >
      {/* Step Number */}
      <TableCell sx={{ textAlign: 'center', py: 2 }}>
            <Box sx={{
              width: 28,
              height: 28,
              borderRadius: '50%',
          backgroundColor: statusColor,
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
          fontSize: '0.75rem',
          fontWeight: 700,
          margin: '0 auto'
            }}>
          {index}
            </Box>
      </TableCell>

      {/* Action */}
      <TableCell sx={{ 
        py: 2,
        textAlign: isRtl ? 'right' : 'left'
      }}>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
          gap: 1,
          flexDirection: isRtl ? 'row-reverse' : 'row',
          justifyContent: isRtl ? 'flex-end' : 'flex-start'
        }}>
          <IconComponent sx={{ 
            fontSize: 16, 
            color: statusColor,
            flexShrink: 0
          }} />
          <Typography variant="body2" sx={{ 
            fontWeight: 600,
            color: statusColor,
            fontSize: '0.85rem'
              }}>
                {typeText}
              </Typography>
        </Box>
      </TableCell>

      {/* Assigned By (User) */}
      <TableCell sx={{ 
        py: 2,
        textAlign: isRtl ? 'right' : 'left'
      }}>
        <Typography variant="body2" sx={{ 
          fontWeight: 500,
          fontSize: '0.8rem',
          color: theme.palette.grey[800],
          direction: isRtl ? 'rtl' : 'ltr'
        }}>
          {event.userName || '-'}
        </Typography>
      </TableCell>

      {/* Details */}
      <TableCell sx={{ 
        py: 2,
        textAlign: isRtl ? 'right' : 'left'
      }}>
        <Box sx={{ 
          fontSize: '0.8rem',
          lineHeight: 1.4,
          color: theme.palette.grey[700],
          direction: isRtl ? 'rtl' : 'ltr',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          alignItems: 'center'
        }}>
          {getDetailsElements().length > 0 ? (
            getDetailsElements().map((element, index) => (
              <React.Fragment key={index}>
                {element}
                {index < getDetailsElements().length - 1 && (
                  <Box component="span" sx={{ color: theme.palette.grey[400], mx: 0.5 }}>
                    •
                  </Box>
                )}
              </React.Fragment>
            ))
          ) : (
            <Typography variant="body2" sx={{ fontSize: '0.8rem', color: theme.palette.grey[500] }}>
              -
              </Typography>
          )}
            </Box>
      </TableCell>

      {/* Date & Time */}
      <TableCell sx={{ 
        py: 2,
        textAlign: isRtl ? 'right' : 'left'
          }}>
            <LocalizedDate 
              date={event.timestamp} 
              typographyProps={{ 
            variant: 'body2', 
            sx: { 
              fontSize: '0.75rem',
              color: theme.palette.grey[600],
              fontWeight: 500,
              direction: 'ltr' // Keep dates LTR for consistency
            }
              }}
              showTime={true}
            />
      </TableCell>

      {/* Comments */}
      <TableCell sx={{ textAlign: 'center', py: 2 }}>
        <CommentSection
          documentId={documentId}
          event={event}
          stepId={stepId}
          comments={comments[documentId] || []}
          loading={loadingComments[documentId]}
          showComments={showComments}
          toggleComments={toggleComments}
          addComment={addComment}
          newComment={newComment}
          setNewComment={setNewComment}
          addingComment={addingComment}
          setAddingComment={setAddingComment}
          theme={theme}
          t={t}
          isRtl={isRtl}
        />
      </TableCell>
    </TableRow>
  );
};

// Professional Comment Section Component
const CommentSection = ({
  documentId,
  event,
  stepId,
  comments,
  loading,
  showComments,
  toggleComments,
  addComment,
  newComment,
  setNewComment,
  addingComment,
  setAddingComment,
  theme,
  t,
  isRtl
}) => {
  const [expanded, setExpanded] = useState(false);
  
  // Filter comments related to this specific event/step
  const eventComments = comments.filter(comment => {
    // Check if comment has metadata with step_id matching this step
    try {
      if (comment.metadata) {
        let metadata = comment.metadata;
        
        // Handle double-encoded JSON string
        if (typeof metadata === 'string') {
          metadata = JSON.parse(metadata);
          // If it's still a string after first parse, parse again
          if (typeof metadata === 'string') {
            metadata = JSON.parse(metadata);
          }
        }
        
        console.log('Checking comment metadata for stepId:', stepId, 'metadata:', metadata);
        return metadata.step_id === stepId;
      }
    } catch (e) {
      console.error('Error parsing comment metadata:', e, 'Raw metadata:', comment.metadata);
    }
    return false;
  });

  console.log(`Step ${stepId} has ${eventComments.length} comments:`, eventComments);

  const handleAddComment = () => {
    console.log('Adding comment for stepId:', stepId);
    console.log('Event details:', event);
    setAddingComment(stepId);
    setExpanded(true);
  };

  const handleSubmitComment = async () => {
    const commentText = newComment[stepId];
    if (!commentText?.trim()) return;
    
    await addComment(documentId, commentText, stepId);
    setExpanded(false);
  };

  const handleCancelComment = () => {
    setNewComment(prev => ({ ...prev, [stepId]: '' }));
    setAddingComment(null);
    setExpanded(false);
  };

  const handleCommentChange = (value) => {
    setNewComment(prev => ({ ...prev, [stepId]: value }));
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
      {/* Comment Button with Badge */}
      <Box sx={{ position: 'relative' }}>
        <Tooltip title={t('tasks:view_comments', 'View Comments')} placement="top">
          <IconButton
            size="small"
            onClick={() => toggleComments(documentId, stepId)}
            sx={{
              color: eventComments.length > 0 ? theme.palette.primary.main : theme.palette.grey[500],
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.08)
              }
            }}
          >
            {eventComments.length > 0 ? (
              <Badge badgeContent={eventComments.length} color="primary" max={99}>
                <CommentIcon sx={{ fontSize: 18 }} />
              </Badge>
            ) : (
              <CommentIcon sx={{ fontSize: 18 }} />
            )}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Add Comment Button */}
      <Tooltip title={t('tasks:add_comment', 'Add Comment')} placement="bottom">
        <IconButton
          size="small"
          onClick={handleAddComment}
          sx={{
            color: theme.palette.success.main,
            '&:hover': {
              backgroundColor: alpha(theme.palette.success.main, 0.08)
            }
          }}
        >
          <AddIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>

      {/* Expanded Comment Form */}
      <Collapse in={expanded && addingComment === stepId}>
        <Paper 
          elevation={2} 
          sx={{ 
            p: 2, 
            mt: 1, 
            minWidth: 300,
            border: `1px solid ${theme.palette.primary.main}`,
            borderRadius: 2
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1.5, color: theme.palette.primary.main, fontWeight: 600 }}>
            {t('tasks:add_step_comment', 'Add Step Comment')}
          </Typography>
          
          <TextField
            fullWidth
            multiline
            rows={3}
            size="small"
            placeholder={t('tasks:comment_placeholder', 'Add additional information about this step...')}
            value={newComment[stepId] || ''}
            onChange={(e) => handleCommentChange(e.target.value)}
            sx={{
              mb: 1.5,
              '& .MuiInputBase-input': {
                textAlign: isRtl ? 'right' : 'left',
                direction: isRtl ? 'rtl' : 'ltr'
              }
            }}
          />
          
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button
              size="small"
              variant="outlined"
              onClick={handleCancelComment}
              sx={{ minWidth: 70 }}
            >
              {t('common:cancel')}
            </Button>
             <Button
               size="small"
               variant="contained"
               onClick={handleSubmitComment}
               disabled={!newComment[stepId]?.trim()}
               startIcon={<SendIcon sx={{ transform: isRtl ? 'scaleX(-1)' : 'none' }} />}
               sx={{ 
                 minWidth: 80,
                 flexDirection: isRtl ? 'row-reverse' : 'row',
                 '& .MuiButton-startIcon': {
                   marginLeft: isRtl ? '12px' : '0px',
                   marginRight: isRtl ? '0px' : '12px'
                 }
               }}
             >
               {t('tasks:add')}
             </Button>
          </Box>
        </Paper>
      </Collapse>

      {/* Comments Display */}
      <Collapse in={showComments[stepId]}>
        <Paper 
          elevation={1} 
          sx={{ 
            mt: 1, 
            maxWidth: 400,
            border: `1px solid ${theme.palette.grey[300]}`,
            borderRadius: 2,
            backgroundColor: theme.palette.grey[50]
          }}
        >
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5, color: theme.palette.grey[700], fontWeight: 600 }}>
              {t('tasks:step_comments', 'Step Comments')} ({eventComments.length})
            </Typography>
            
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <Skeleton variant="text" width="80%" height={20} />
              </Box>
            ) : eventComments.length > 0 ? (
              <Stack spacing={1.5} sx={{ maxHeight: 200, overflowY: 'auto' }}>
                {eventComments.map((comment) => (
                  <Box
                    key={comment.id}
                    sx={{
                      p: 1.5,
                      borderRadius: 1.5,
                      backgroundColor: 'white',
                      border: `1px solid ${theme.palette.grey[200]}`,
                      textAlign: isRtl ? 'right' : 'left'
                    }}
                  >
                    {/* Username - Highlighted */}
                    <Typography variant="body2" sx={{ 
                      mb: 0.5, 
                      fontSize: '0.8rem', 
                      fontWeight: 700,
                      color: theme.palette.primary.main,
                      lineHeight: 1.3
                    }}>
                      {comment.user?.full_name || comment.user?.username || t('common:unknown')}
                    </Typography>
                    
                    {/* Department name if available */}
                    {comment.user?.department?.name && (
                      <Typography variant="caption" sx={{ 
                        mb: 0.5, 
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        color: theme.palette.info.main,
                        display: 'block'
                      }}>
                        {comment.user.department.name}
                      </Typography>
                    )}
                    
                    {/* Comment content */}
                    <Typography variant="body2" sx={{ 
                      mb: 0.5, 
                      fontSize: '0.8rem', 
                      lineHeight: 1.4,
                      color: theme.palette.text.primary
                    }}>
                      {comment.content}
                    </Typography>
                    
                    {/* Date and time - Smaller */}
                    <Typography variant="caption" color="text.secondary" sx={{ 
                      fontSize: '0.55rem',
                      display: 'block',
                      textAlign: isRtl ? 'left' : 'right'
                    }}>
                      <LocalizedDate date={comment.created_at} showTime={true} />
                    </Typography>
                  </Box>
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 1, fontSize: '0.75rem' }}>
                {t('tasks:no_comments', 'No comments yet')}
              </Typography>
            )}
          </Box>
        </Paper>
      </Collapse>
    </Box>
  );
}; 